import React, { Suspense, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useLocation, useParams } from 'react-router-dom';
import { Button, IconButton } from '../../../../components/ui';
import { FeatureThemeProvider } from '../../../../theme/FeatureThemeProvider';
import useBusinessFunctionsSx from '../../hooks/useBusinessFunctionsSx';
import ImportRecordsUploadPanel from '../components/import/ImportRecordsUploadPanel';
import ImportExternalSourcesDialog from '../components/import/ImportExternalSourcesDialog';
import useImportRecordsUpload from '../hooks/useImportRecordsUpload';
import { RECORD_COPY } from '../constants/recordCopy';
import useRecordPermissions from '../hooks/useRecordPermissions';

function ImportRecordsPageContent() {
  const styles = useBusinessFunctionsSx();
  const location = useLocation();
  const { campaignId: workspaceId, leadFormId: tableId } = useParams();
  const [externalOpen, setExternalOpen] = useState(false);

  const returnState = location.state ?? {};
  const permissions = useRecordPermissions(workspaceId, returnState.accountId);

  const upload = useImportRecordsUpload({
    leadsFormId: tableId,
    returnState: {
      accountId: returnState.accountId,
      workspaceId: returnState.workspaceId ?? workspaceId,
    },
  });

  if (!permissions.canImport) {
    return (
      <Box sx={styles.pageRoot}>
        <Typography role="alert" color="error">
          You do not have permission to import records for this business service.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageRoot}>
      <Box sx={styles.importRecordsHeaderRow}>
        <Box sx={styles.pageHeader}>
          <Typography variant="h5" sx={styles.pageTitle}>
            {RECORD_COPY.import.title}
          </Typography>
          <Typography variant="body2" sx={styles.pageSubtitle}>
            {RECORD_COPY.import.subtitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="secondary"
            icon={<AddOutlinedIcon />}
            onClick={() => setExternalOpen(true)}
          >
            {RECORD_COPY.import.importOtherSources}
          </Button>
          <IconButton
            aria-label={RECORD_COPY.import.back}
            variant="ghost"
            onClick={upload.navigateBack}
          >
            <ArrowBackOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <ImportRecordsUploadPanel
        styles={styles}
        selectedFile={upload.selectedFile}
        errorMessage={upload.errorMessage}
        errorCells={upload.errorCells}
        isUploading={upload.isUploading}
        isDownloadingSample={upload.isDownloadingSample}
        isDownloadingErrors={upload.isDownloadingErrors}
        onFileSelect={upload.handleFileSelect}
        onDownloadSample={upload.handleDownloadSample}
        onDownloadErrorFile={upload.handleDownloadErrorFile}
        onUpload={upload.handleUpload}
      />

      <ImportExternalSourcesDialog
        open={externalOpen}
        onClose={() => setExternalOpen(false)}
      />
    </Box>
  );
}

export default function ImportRecordsPage() {
  const { campaignId } = useParams();

  return (
    <FeatureThemeProvider feature="business-functions">
      <Suspense fallback={null}>
        <ImportRecordsPageContent key={campaignId} />
      </Suspense>
    </FeatureThemeProvider>
  );
}
